import * as React from "react";

import { getTurnstileSiteKey, loadTurnstile } from "utils/turnstile";

type TurnstileWidgetProps = {
  className?: string;
  onTokenChange?: (token: string | null) => void;
  onLoadError?: () => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible";
};

export type TurnstileWidgetHandle = {
  reset: () => void;
};

export const TurnstileWidget = React.forwardRef<
  TurnstileWidgetHandle,
  TurnstileWidgetProps
>(({ className, onTokenChange, onLoadError, theme = "auto", size = "normal" }, ref) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const widgetIdRef = React.useRef<string | null>(null);
  const onTokenChangeRef = React.useRef(onTokenChange);
  const onLoadErrorRef = React.useRef(onLoadError);

  React.useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  React.useEffect(() => {
    onLoadErrorRef.current = onLoadError;
  }, [onLoadError]);

  React.useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
        onTokenChangeRef.current?.(null);
      },
    }),
    [],
  );

  React.useEffect(() => {
    let isMounted = true;

    const renderWidget = async () => {
      try {
        await loadTurnstile();
      } catch (error) {
        console.error("[Turnstile] script load failed", error);
        onLoadErrorRef.current?.();
        return;
      }

      if (!isMounted || !containerRef.current || !window.turnstile) {
        return;
      }

      const sitekey = getTurnstileSiteKey();
      if (!sitekey) {
        console.error("[Turnstile] sitekey not found");
        onLoadErrorRef.current?.();
        return;
      }

      const id = window.turnstile.render(containerRef.current, {
        sitekey,
        theme,
        size,
        appearance: "always",
        callback: (token) => {
          onTokenChangeRef.current?.(token);
        },
        "expired-callback": () => {
          onTokenChangeRef.current?.(null);
        },
        "error-callback": () => {
          onTokenChangeRef.current?.(null);
          onLoadErrorRef.current?.();
        },
      });

      widgetIdRef.current = id;
    };

    renderWidget();

    return () => {
      isMounted = false;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [theme, size]);

  return <div ref={containerRef} className={className} aria-live="polite" />;
});

TurnstileWidget.displayName = "TurnstileWidget";

export default TurnstileWidget;
